import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faSave, faUserCircle, faCaretDown, faUser, 
  faSyncAlt, faSignOutAlt, faCrown, faCheckCircle, 
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import '../scss/MachineConfig.scss';
import BaseURL from '../assets/contants/BaseURL';

const MachineConfig = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState({
    machineId: '',
    deviceId: '',
    machineName: '',
    manufacturer: '',
    model: '',
    line: '',
    maxCapacity: 1,
    swl: '',
    maxHoistHeight: 1,
    hoistLoadPower: 1,
    ctLoadPower: 1,
    ltLoadPower: 1,
    stage1Threshold: '',
    stage2Threshold: '',
    craneSpeed: '',
    energyCost: 0.00
  });
  
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [machineData, setMachineData] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null);

  const getAuthToken = () => sessionStorage.getItem('Token') || localStorage.getItem('Token');

  const getAuthHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Token ${token}`;
    }
    
    return headers;
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  useEffect(() => {
    const fetchOrgLogo = async () => {
      try {
        const token = getAuthToken();
        const organizationId = sessionStorage.getItem('organization_id');

        const response = await fetch(`${BaseURL}Userauth/organizations/${organizationId}/`, {
          headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': 'application/json',
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.logo_url) { 
            setOrgLogo(data.data.logo_url);
          }
        }
      } catch (error) {
        console.error('Error fetching org logo:', error);
      }
    };

    fetchOrgLogo();
  }, []);

  const fetchDevices = useCallback(async () => {
    try {
      const response = await fetch(`${BaseURL}meters/device/`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        navigate('/login');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        const deviceList = Array.isArray(data) ? data : (data.results || data.devices || []);
        setDevices(deviceList);
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      showNotification('Failed to load devices', 'error');
    }
  }, [navigate]);

  const fetchMachineData = useCallback(async () => {
    if (!isEditMode) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BaseURL}meters/cms-cranes/${id}/`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setMachineData(data);
        
        // Populate form with machine data
        setFormData({
          machineId: data.machine_id || '',
          deviceId: data.device_name || '',
          machineName: data.machine_name || '',
          manufacturer: data.manufacture || '',
          model: data.model || '',
          line: data.line || '',
          maxCapacity: data.max_capacity || 1,
          swl: data.safe_weight_load || '',
          maxHoistHeight: data.max_hoist_height || 1,
          hoistLoadPower: data.hoist_load_power || 1,
          ctLoadPower: data.ct_load_power || 1,
          ltLoadPower: data.lt_load_power || 1,
          stage1Threshold: data.warning_threshold || '',
          stage2Threshold: data.upper_threshold || '',
          craneSpeed: data.crane_speed || '',
          energyCost: data.energy_cost || 0.00
        });
      } else if (response.status === 404) {
        showNotification('Machine not found', 'error');
        navigate('/machine');
      }
    } catch (error) {
      console.error('Error fetching machine data:', error);
      showNotification('Failed to load machine data', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [id, isEditMode, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = {
      machine_name: formData.machineName,
      machine_id: formData.machineId,
      line: formData.line,
      manufacture: formData.manufacturer,
      model: formData.model,
      max_capacity: parseInt(formData.maxCapacity) || 1,
      max_hoist_height: parseInt(formData.maxHoistHeight) || 1,
      safe_weight_load: parseFloat(formData.swl) || 0,
      hoist_load_power: parseInt(formData.hoistLoadPower) || 1,
      ct_load_power: parseInt(formData.ctLoadPower) || 1,
      lt_load_power: parseInt(formData.ltLoadPower) || 1,
      warning_threshold: parseFloat(formData.stage1Threshold) || 0,
      upper_threshold: parseFloat(formData.stage2Threshold) || 0,
      crane_speed: parseFloat(formData.craneSpeed) || 0,
      device_name: formData.deviceId ? parseInt(formData.deviceId) : null,
      energy_cost: parseFloat(formData.energyCost) || 0.00
    };

    setIsLoading(true);
    
    try {
      const url = isEditMode ? `${BaseURL}meters/cms-cranes/${id}/` : `${BaseURL}meters/cms-cranes/`;
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method: method,
        headers: getAuthHeaders(),
        body: JSON.stringify(submitData)
      });
      
      if (response.status === 401) {
        showNotification('Session expired. Please login again.', 'error');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        return;
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save machine configuration');
      }
      
      showNotification(
        `Machine ${isEditMode ? 'updated' : 'created'} successfully!`, 
        'success'
      );
      
      setTimeout(() => {
        navigate('/machine');
      }, 1500);
    } catch (error) {
      console.error('Error saving machine:', error);
      showNotification(
        error.message || `Failed to ${isEditMode ? 'update' : 'create'} machine configuration`,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      navigate('/machine');
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      sessionStorage.removeItem('Token');
      sessionStorage.removeItem('session_id');
      sessionStorage.removeItem('username');
      localStorage.removeItem('Token');
      localStorage.removeItem('session_id');
      localStorage.removeItem('username');
      
      showNotification('Logged out successfully!', 'success');
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    }
  };

  useEffect(() => {
    fetchDevices();
    if (isEditMode) {
      fetchMachineData();
    }
  }, [fetchDevices, fetchMachineData, isEditMode]);

  return (
    <div className="machine-config-container">
      <div className="dashboard-content">
        {/* Header */}
        <div className="header">
          <div className="header-left">
            <div className="header-logo">CraneIQ</div>
          </div>

          <div className="user-info-wrapper">
            <div className="account-dropdown">
              <div className="account-trigger" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                {orgLogo ? (
                                  <img
                                    src={orgLogo}
                                    alt="Organization Logo"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '50%',
                                      objectFit: 'cover',
                                      border: '2px solid #4361ee'
                                    }}
                                    onError={() => setOrgLogo(null)}
                                  />
                                ) : (
                                  <FontAwesomeIcon icon={faUserCircle} style={{ fontSize: '1.5rem', color: '#4361ee' }} />
                                )}
                <span>{sessionStorage.getItem('username') || 'User'}</span>
                <FontAwesomeIcon icon={faCaretDown} />
              </div>
              {isDropdownOpen && (
                              <div className="account-menu show">
                                <div className="account-menu-item plan-info">
                                  <FontAwesomeIcon icon={faCrown} style={{ color: 'gold' }} />
                                  <span>Basic Plan</span>
                                </div>
                                <div className="account-menu-divider"></div>
                                <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                  <FontAwesomeIcon icon={faUser} /> Profile
                                </button>
                                <button className="account-menu-item" style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer' }}>
                                  <FontAwesomeIcon icon={faSyncAlt} /> Upgrade Plan
                                </button>
                                <div className="account-menu-divider"></div>
                                <button className="account-menu-item logout-account-btn" onClick={handleLogout}>
                                  <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                                </button>
                              </div>
                            )}
            </div>
          </div>
        </div>

        {/* Page Title */}
        <div className="page-titles">
          <h1>{isEditMode ? 'Edit Machine Configuration' : 'Machine Configuration'}</h1>
          <p>{isEditMode ? `Editing ${machineData?.machine_name || 'machine'}` : 'Add new machine configuration'}</p>
        </div>

        {/* Form Container */}
        <div className="form-container">
          {isLoading && !machineData && isEditMode ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading machine data...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h3 className="section-header">Basic Details</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="machineId">Machine ID</label>
                  <input
                    type="text"
                    id="machineId"
                    name="machineId"
                    placeholder="MAC5632"
                    value={formData.machineId}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="deviceId">Device ID</label>
                  <select
                    id="deviceId"
                    name="deviceId"
                    className="filter-select" 
                    value={formData.deviceId}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Device</option>
                    {devices
                      .filter(device => !device.device_token?.toLowerCase().startsWith('remote_'))
                      .map(device => (
                        <option key={device.id} value={device.id}>
                          {device.device_name}
                        </option>
                      ))
                    }
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="machineName">Name</label>
                  <input
                    type="text"
                    id="machineName"
                    name="machineName"
                    value={formData.machineName}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="manufacturer">Manufacturer</label>
                  <input
                    type="text"
                    id="manufacturer"
                    name="manufacturer"
                    value={formData.manufacturer}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="model">Model</label>
                  <input
                    type="text"
                    id="model"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="line">Line</label>
                  <input
                    type="text"
                    id="line"
                    name="line"
                    value={formData.line}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <h3 className="section-header">Capacity Settings</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="maxCapacity">Max Capacity (tons)</label>
                  <input
                    type="number"
                    id="maxCapacity"
                    name="maxCapacity"
                    min="1"
                    value={formData.maxCapacity}
                    onChange={handleInputChange}
                    required
                  />
                  <small>Maximum load capacity of the crane</small>
                </div>
                
                <div className="form-group">
                  <label htmlFor="swl">Safe Working Load (kg)</label>
                  <input
                    type="number"
                    id="swl"
                    name="swl"
                    value={formData.swl}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="maxHoistHeight">Max Hoist Height (meters)</label>
                  <input
                    type="number"
                    id="maxHoistHeight"
                    name="maxHoistHeight"
                    min="1"
                    value={formData.maxHoistHeight}
                    onChange={handleInputChange}
                    required
                  />
                  <small>Maximum vertical lifting height</small>
                </div>
                
                <div className="form-group">
                  <label htmlFor="hoistLoadPower">Hoist Load Power (kW)</label>
                  <input
                    type="number"
                    id="hoistLoadPower"
                    name="hoistLoadPower"
                    min="1"
                    value={formData.hoistLoadPower}
                    onChange={handleInputChange}
                    required
                  />
                  <small>Hoist mechanism power rating</small>
                </div>
                
                <div className="form-group">
                  <label htmlFor="ctLoadPower">CT Load Power (kW)</label>
                  <input
                    type="number"
                    id="ctLoadPower"
                    name="ctLoadPower"
                    min="1"
                    value={formData.ctLoadPower}
                    onChange={handleInputChange}
                    required
                  />
                  <small>Cross Travel mechanism power rating</small>
                </div>
                
                <div className="form-group">
                  <label htmlFor="ltLoadPower">LT Load Power (kW)</label>
                  <input
                    type="number"
                    id="ltLoadPower"
                    name="ltLoadPower"
                    min="1"
                    value={formData.ltLoadPower}
                    onChange={handleInputChange}
                    required
                  />
                  <small>Long Travel mechanism power rating</small>
                </div>
                
                <div className="form-group">
                  <label htmlFor="stage1Threshold">Warning Threshold</label>
                  <input
                    type="number"
                    id="stage1Threshold"
                    name="stage1Threshold"
                    value={formData.stage1Threshold}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="stage2Threshold">Upper Threshold</label>
                  <input
                    type="number"
                    id="stage2Threshold"
                    name="stage2Threshold"
                    value={formData.stage2Threshold}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group full-width">
                  <label htmlFor="craneSpeed">Crane Speed (m/min)</label>
                  <input
                    type="number"
                    id="craneSpeed"
                    name="craneSpeed"
                    value={formData.craneSpeed}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="energyCost">Energy Cost (Rs/hour)</label>
                  <input
                    type="number"
                    id="energyCost"
                    name="energyCost"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={formData.energyCost}
                    onChange={handleInputChange}
                    required
                  />
                  <small>Cost per hour of operation</small>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isLoading}>
                  <FontAwesomeIcon icon={faSave} /> {isLoading ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`notification notification-${notification.type} show`}>
          <FontAwesomeIcon icon={notification.type === 'success' ? faCheckCircle : faExclamationCircle} />
          {notification.message}
        </div>
      )}
    </div>
  );
};

export default MachineConfig;